import React from 'react';

import RetrievePhone from 'components/RetrievePhone';
import Address from 'components/Steps/Address';
import AddressMap from 'components/Steps/Address/AddressMap';
import AddressSuggest from 'components/Steps/Address/AddressSuggest';
import FakeAnimation from 'components/Steps/FakeAnimation';
import More from 'components/Steps/More';
import OptionalFeatures from 'components/Steps/OptionalFeatures';
import ProjectDefinition from 'components/Steps/ProjectDefinition';
import PropertyInformations from 'components/Steps/PropertyInformations';
import PropertyInformationsTips from 'components/Steps/PropertyInformations/Tips';
import PropertyType from 'components/Steps/PropertyType';
import { FORM_ADDRESS_NAME, FORM_CITY_NAME, FORM_ZIP } from 'constants/Global';
import {
    addressStepSubtitle,
    hasPhoneRetrieval,
    hasSuggestedAddresses,
} from 'helpers/FormInformations';
import { translate } from 'helpers/Translate';

/**
 * EstimaForm step ids.
 * @type {Object}
 */
export const STEP_IDS = {
    address: 'address',
    addressSuggest: 'address-suggest',
    type: 'type',
    facts: 'facts',
    features: 'features',
    more: 'more',
    project: 'project',
    fakeAnimation: 'fake-animation',
    login: 'login',
    retrievePhone: 'retrieve-phone',
};

/**
 * EstimaForm steps.
 * @type {Array}
 */
export const STEPS = [
    {
        id: STEP_IDS.address,
        taggingId: 'address',
        title: {
            desktop: translate('Étape 1 : Adresse du bien'),
            mobile: translate('Adresse du bien'),
        },
        subtitle: addressStepSubtitle,
        sidePanel: <AddressMap />,
        component: <Address />,
        fields: [FORM_ADDRESS_NAME, FORM_CITY_NAME, FORM_ZIP],
        disableAsyncValidate: true,
    },
    {
        id: STEP_IDS.addressSuggest,
        condition: hasSuggestedAddresses,
        title: {
            desktop: translate('Étape 1 : Adresse du bien'),
            mobile: translate('Adresse du bien'),
        },
        subtitle: translate('Nous avons trouvé une adresse similaire'),
        sidePanel: <AddressMap />,
        component: <AddressSuggest />,
        fields: [FORM_ADDRESS_NAME, FORM_CITY_NAME, FORM_ZIP],
        disableAsyncValidate: true,
    },
    {
        id: STEP_IDS.type,
        taggingId: 'type',
        checkZoneMedia: true,
        title: {
            desktop: translate('Étape 2 : À propos de ce bien'),
            mobile: translate('À propos de ce bien'),
        },
        subtitle: translate("De quel type de bien s'agit-il ?"),
        iconInformation: true,
        informations: translate(
            'Moins de 2 minutes suffisent pour obtenir l’estimation personnalisée de votre bien.',
        ),
        disclaimer: false,
        component: <PropertyType />,
        fields: ['item_type', 'item_subtype'],
    },
    {
        id: STEP_IDS.facts,
        taggingId: 'facts',
        title: {
            desktop: translate('Étape 2 : À propos de ce bien'),
            mobile: translate('À propos de ce bien'),
        },
        subtitle: translate('Informations principales du bien'),
        iconInformation: false,
        informations: <PropertyInformationsTips />,
        disclaimer: false,
        component: <PropertyInformations />,
        fields: [
            'area',
            'room_count',
            'bathroom_count',
            'floor',
            'floor_count',
            'ground_garden',
            'ground_garden_area',
            'level_count',
            'site_area',
            'site_buildable_area',
        ],
    },
    {
        id: STEP_IDS.features,
        taggingId: 'features',
        defaultValid: true,
        optional: true,
        title: {
            desktop: translate('Étape 2 : À propos de ce bien'),
            mobile: translate('À propos de ce bien'),
        },
        subtitle: translate('Caractéristiques du bien'),
        iconInformation: true,
        informations: translate(
            "Pour recevoir l'estimation la plus précise, n'hésitez pas à renseigner un maximum de caractéristiques sur votre bien.",
        ),
        disclaimer: false,
        component: <OptionalFeatures />,
        fields: [
            'shared_wall',
            'shared_wall_count',
            'elevator',
            'balcony',
            'balcony_area',
            'terrace',
            'terrace_area',
            'cellar',
            'cellar_count',
            'basement',
            'basement_area',
            'parking',
            'parking_count',
            'secondary_building',
            'secondary_building_count',
            'secondary_room',
            'secondary_room_count',
            'exceptional_view',
            'swimming_pool',
            'sewerage',
        ],
    },
    {
        id: STEP_IDS.more,
        taggingId: 'more',
        defaultValid: true,
        optional: true,
        title: {
            desktop: translate('Étape 2 : À propos de ce bien'),
            mobile: translate('À propos de ce bien'),
        },
        subtitle: translate('Précisions concernant votre bien'),
        iconInformation: true,
        informations: translate(
            'Vous ne savez pas répondre ? Rassurez-vous, cette étape est optionelle. Vous pourrez modifier les informations ultérieurement.',
        ),
        disclaimer: false,
        component: <More />,
        fields: [
            'build_period',
            'build_date',
            'energy_consumption',
            'standing',
            'relative_perception',
            'lightness',
            'peacefulness',
            'transport_proximity',
            'roof_quality',
            'renovation_level',
        ],
    },
    {
        id: STEP_IDS.project,
        taggingId: 'project',
        checkAuth: true,
        title: {
            desktop: translate('Étape 3 : Ce bien et vous'),
            mobile: translate('Ce bien et vous'),
        },
        subtitle: translate('Ce bien et vous'),
        iconInformation: true,
        informations: translate(
            'Plus vous définissez votre projet, plus nous pouvons vous donner les clés pour faire de ce projet une réussite en vous proposant les solutions les plus adaptées.',
        ),
        disclaimer: false,
        component: <ProjectDefinition />,
        initialStatus: false,
        fields: (values) => [
            'profile_owner',
            'profile_seller',
            'profile_sale_method',
            'profile_buyer',
            'profile_needs_to_sell',
            'user_profile',
            'user_profile_realtor_positions',
            'profile_incentive',
            'profile_needs_loan',
            'profile_needs_loan_status',
            'profile_residence_type',
            values._zoneMedia ? null : 'profile_optin_realtors',
            'user_phone',
        ],
    },
    {
        id: STEP_IDS.fakeAnimation,
        condition: ({ modifier }) => modifier > 0,
        defaultValid: true,
        title: {
            desktop: translate('Étape 4 : Découvrez votre estimation'),
            mobile: translate('Découvrez votre estimation'),
        },
        subtitle: '',
        hideNavigation: true,
        iconInformation: false,
        component: <FakeAnimation />,
        fields: [],
    },
    {
        id: STEP_IDS.login,
        defaultValid: true,
        condition: ({ auth }) => auth.needLogin,
        title: {
            desktop: translate('Étape 4 : Découvrez votre estimation'),
            mobile: translate('Découvrez votre estimation'),
        },
        subtitle: translate('Votre estimation est prête !'),
        descriptionText: true,
        iconInformation: false,
        disclaimer: true,
        fields: [],
        disableAsyncValidate: true,
    },
    {
        id: STEP_IDS.retrievePhone,
        taggingId: 'retrieve-phone',
        defaultValid: true,
        checkAuth: true,
        condition: hasPhoneRetrieval,
        title: {
            desktop: translate('Étape 4 : Découvrez votre estimation'),
            mobile: translate('Découvrez votre estimation'),
        },
        subtitle: translate('Dernière étape avant votre estimation : vérifiez vos informations'),
        component: <RetrievePhone />,
        fields: [],
        disableAsyncValidate: true,
    },
];

/**
 * Number of user steps.
 * @type {Number}
 */
export const STEPS_COUNT = STEPS.length;
